import type { ReactNode } from 'react';

/**
 * Accessible form field wrapper.
 *
 * Closes AUD3-015 (R-6/R-7): every field in production's quote and contact forms
 * had name=null and no associated <label for>. That broke browser autofill,
 * password managers, native form submission and screen-reader labelling. Here a
 * `name` and a linked `<label>` are structurally required — you cannot render a
 * field without them.
 *
 * Also closes R-8: errors are announced via aria-describedby + aria-invalid.
 */

export function Field({
  name,
  label,
  error,
  required,
  hint,
  children,
}: {
  name: string;
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: (props: {
    id: string;
    name: string;
    required: boolean;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
    className: string;
  }) => ReactNode;
}) {
  const id = `field-${name}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        {required && (
          <>
            {' '}
            <span className="text-green-accent" aria-hidden="true">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </label>
      {children({
        id,
        name,
        required: Boolean(required),
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy,
        className: 'field-input',
      })}
      {hint && (
        <p id={hintId} className="mt-1 text-sm text-charcoal/60">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
